import { Activity, ArrowUpRight } from "lucide-react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { NotFoundPage } from "@/features/landing/pages/NotFoundPage";
import { isAdminHost } from "@/lib/hostKind";
import { SuperAdminSidebar } from "./components/SuperAdminSidebar";
import { superAdminSectionForPath } from "./nav";
import {
  useOperatorDiagnostics,
  useOperatorStreams,
} from "./hooks/useOperatorData";

/**
 * Platform super-admin console shell (multi-club).
 *
 * Lives at the OPERATOR host only (`admin.<apex>`, e.g. admin.tktic.com) —
 * not the apex, which is marketing-only. Gates on `is_super_admin` from
 * /api/me — the AUTHORITATIVE source, since the backend also refuses
 * super-admin data on a club subdomain. The host check is a UX nicety; the
 * server is the real gate.
 *
 * Distinct from /admin, which is the per-CLUB admin console. Scope
 * legibility is deliberate (admin-console-split PR 4): this shell uses the
 * Platform navigation item and NO club branding, so an operator with both
 * consoles open can never mistake which tenant they're acting on. A slim
 * platform-health strip in the header keeps the operator's one question —
 * "is the platform healthy?" — answered on every section.
 */
export function SuperAdminShell() {
  const meQuery = useMe();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const hydrating = useAuthStore((s) => s.hydrating);
  const pathname = useLocation({ select: (l) => l.pathname });

  if (hydrating || meQuery.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-xs font-mono text-ink-muted">
        Loading…
      </div>
    );
  }

  // The operator console lives on the ADMIN host only (`admin.<apex>`). Any
  // other host 404s:
  //   - a CLUB host, because the backend 404s every /api/super-admin/* call
  //     there, so the shell would render an empty broken page;
  //   - the APEX, because it is marketing-only and the operator surface is
  //     deliberately consolidated on one host (it used to render here, which
  //     made "which host owns the console?" answerable two ways — see #593).
  //
  // `hostKind()` is safe to trust here even though it is build-time-derived:
  // `apexDomain()` adopts the backend's runtime apex when the two disagree
  // (see `adoptApexDomain`), so a wrong VITE_CLUB_APEX_DOMAIN can no longer
  // misclassify this host and 404 the console. Dev/e2e reach this via
  // VITE_DEV_ADMIN_HOST_OVERRIDE.
  if (!isAdminHost()) {
    return <NotFoundPage />;
  }

  if (!isSuperAdmin) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="font-display text-lg text-ink">Super admin only</h1>
        <p className="mt-2 text-sm text-ink-muted">
          This console manages every club on the platform. It’s available to
          platform operators on the admin host.
        </p>
      </div>
    );
  }

  const active = superAdminSectionForPath(pathname);

  return (
    <main
      data-testid="super-admin-shell"
      className="platform-console bg-canvas"
    >
      <h1 className="sr-only">Super Admin Console</h1>
      <PlatformHealthStrip />
      <div className="platform-layout">
        <SuperAdminSidebar active={active} />
        <section
          data-testid="super-admin-content"
          data-super-admin-section={active}
          className="platform-content"
        >
          <Outlet />
        </section>
      </div>
    </main>
  );
}

/**
 * The operator's constant: four platform vitals on every section, linking
 * into Operations for the detail. Flat — no card chrome; the strip encodes
 * the console's job, it doesn't decorate it.
 */
function PlatformHealthStrip() {
  const diagnostics = useOperatorDiagnostics().data;
  const streams = useOperatorStreams().data;

  const failed = diagnostics?.counts.failed_slots;
  const transcoding = diagnostics?.counts.transcoding_slots;
  const diskFree = diagnostics?.disk.free_bytes;
  const viewers = streams?.active.length;

  return (
    <div
      data-testid="platform-health-strip"
      className="platform-health-strip"
      aria-label="Platform status"
    >
      <strong className="platform-health-title">
        <Activity aria-hidden /> Platform status
      </strong>
      <HealthStat
        label="failed slots"
        value={failed}
        alert={typeof failed === "number" && failed > 0}
      />
      <HealthStat label="encoding" value={transcoding} />
      <HealthStat label="viewers" value={viewers} />
      <HealthStat
        label="disk free"
        value={
          typeof diskFree === "number" ? formatBytesShort(diskFree) : undefined
        }
        alert={diagnostics ? !diagnostics.disk.enough_space : false}
      />
      <Link to="/super-admin/operations" className="platform-health-link">
        Open health <ArrowUpRight aria-hidden />
      </Link>
    </div>
  );
}

function HealthStat({
  label,
  value,
  alert = false,
}: {
  label: string;
  value: number | string | undefined;
  alert?: boolean;
}) {
  return (
    <span className="flex items-baseline gap-1 whitespace-nowrap">
      <span
        className={alert ? "font-semibold text-destructive-text" : "text-ink"}
      >
        {value ?? "–"}
      </span>
      <span className="text-ink-muted">{label}</span>
    </span>
  );
}

function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)}${units[unit]}`;
}
